"use client";
import { useState, useEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import type { GameType, Category } from "@/lib/types";
import { fetchGamesByRange } from "@/lib/games"
import Swal from "sweetalert2";

interface GameGridProps {
  games: GameType[];
  categories?: Category[];
  selectedCategory?: string | null;
  expandable?: boolean;
  page?: number | null;
  errorMsg?: string | null;
  unit?: number;
}

export default function GameGrid({
  games,
  categories,
  selectedCategory,
  unit = 12, // Default value if not provided
  expandable = false,
  page,
  errorMsg: initialErrorMsg
}: GameGridProps) {
  const initialDisplayedGames = games;
  const [additionalDisplayedGames, setAdditionalDisplayedGames] = useState<GameType[]>([]);
  const [depth, setDepth] = useState(1);
  const [currentErrorMsg, setCurrentErrorMsg] = useState<string | null>(initialErrorMsg || null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const onCategoryButtonClicked = () => {
    setDepth(1);
    setAdditionalDisplayedGames([]);
    setCurrentErrorMsg(null); // Reset error on category change
  };

  const onLoadMoreGamesButtonClicked = async () => {
    if (isLoadingMore) return; // Prevent multiple clicks
    setIsLoadingMore(true);
    setCurrentErrorMsg(null); // Reset error before fetching

    try {
      const newGames = await fetchGamesByRange(selectedCategory ?? null, (depth * unit), (depth + 1) * unit - 1);
      if (newGames.length > 0) {
        setAdditionalDisplayedGames(prev => [...prev, ...newGames]);
        setDepth(depth + 1); // Increment depth only if games were fetched
      }
    } catch (err) {
      console.error("게임 데이터 가져오기 실패:", err);
      const errorText = err instanceof Error ? err.message : "게임 데이터를 불러오는 데 실패했습니다.";
      setCurrentErrorMsg(errorText); // Set error message to state
    } finally {
      setIsLoadingMore(false);
    }
  };

  // Display error message using Swal when it changes
  useEffect(() => {
    if (currentErrorMsg) {
      Swal.fire({
        icon: "error",
        title: "오류 발생",
        text: currentErrorMsg,
        confirmButtonText: "확인",
      }).then(() => {
        setCurrentErrorMsg(null); // Reset error after showing
      });
    }
  }, [currentErrorMsg]);

  // Combine initial and additional games for rendering
  const allGames = [...initialDisplayedGames, ...additionalDisplayedGames];

  return (
    <div className="game-grid-container">
      {/* Category Buttons */}
      <div className="category-buttons mb-6 flex flex-wrap gap-2 justify-center">
        <Link href="/games">
          <Button
            onClick={onCategoryButtonClicked}
            variant={selectedCategory === null || selectedCategory === undefined ? "default" : "outline"}
            className={`category-btn ${selectedCategory === null || selectedCategory === undefined ? 'category-btn-active' : ''}`}
            size="sm"
          >
            전체
          </Button>
        </Link>
        {(categories || [])
          .filter((category) => category.name !== "0") // Filter out category "0"
          .map((category) => (
            <Link key={category.name} href={`/games?category=${encodeURIComponent(category.name)}`}>
              <Button
                onClick={onCategoryButtonClicked}
                variant={selectedCategory === category.name ? "default" : "outline"}
                className={`category-btn ${selectedCategory === category.name ? 'category-btn-active' : ''}`}
                size="sm"
              >
                {category.name}
              </Button>
            </Link>
          ))}
      </div>

      {/* Game Card List */}
      {allGames.length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-6">
            {allGames.map((game) => (
              <Link key={game.id} href={`/games/${game.slug}`} className="block group game-card-link">
                <Card className="game-card overflow-hidden h-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg transition-all duration-300 ease-in-out">
                  <div className="relative w-full aspect-[4/3] overflow-hidden"> {/* Changed aspect ratio */}
                    <Image
                      src={game.thumbnail || "/placeholder.png"}
                      alt={game.title}
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = "/placeholder.png";
                      }}
                      fill // Use fill for responsive images
                      sizes="(max-width: 640px) 50vw, (max-width: 768px) 50vw, (max-width: 1024px) 25vw, 16.6vw" // Define sizes for optimization
                      className="game-thumbnail object-cover transition-transform duration-500 ease-in-out"
                    />
                    {game.isNew && (
                      <Badge className="new-badge absolute top-2 right-2 px-2 py-0.5 text-xs" variant="secondary">
                        NEW ✨
                      </Badge>
                    )}
                     <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </div>
                  <CardContent className="p-3"> {/* Adjusted padding */}
                    <h3 className="game-title font-semibold line-clamp-1 text-sm md:text-base text-orange-700 dark:text-orange-400 group-hover:text-orange-600 dark:group-hover:text-orange-300 transition-colors">
                      {game.title}
                    </h3>
                    <p className="game-description text-xs md:text-sm text-gray-600 dark:text-gray-400 line-clamp-2 mt-1">
                      {game.description}
                    </p>
                  </CardContent>
                   {/* Footer can be optional or simplified */}
                   {/* <CardFooter className="p-3 pt-0 flex justify-start">
                      {game.categories?.[0] && (
                          <Badge variant="outline" className="category-tag text-xs">{game.categories[0]}</Badge>
                      )}
                   </CardFooter> */}
                </Card>
              </Link>
            ))}
          </div>
        ) : (
           !initialErrorMsg && <p className="text-center text-gray-500 dark:text-gray-400 py-10">이 카테고리에는 게임이 없습니다.</p>
        )
       }

      {/* Load More Button */}
      {expandable && allGames.length >= depth * unit && ( // Show button only if there potentially are more games
        <div className="mt-8 flex justify-center">
          <Button onClick={onLoadMoreGamesButtonClicked} disabled={isLoadingMore} className="load-more-btn">
            {isLoadingMore ? (
              <>
                <span className="spinner mr-2"></span> 로딩 중...
              </>
            ) : (
              '더보기'
            )}
          </Button>
        </div>
      )}

      {/* SEO Links (unchanged) */}
      {expandable && page === null && (
        <div className="hidden">{/*"post"*/}
          <a href={`/games?category=${selectedCategory}&page=2&seo=true`}></a>
        </div>
      )}
      {expandable && typeof page === 'number' && page > 0 && (
        <div className="hidden">{/*"post"*/}
          <a href={`/games?category=${selectedCategory}&page=${page + 1}&seo=true`}></a>
        </div>
      )}
      {typeof page === 'number' && page > 1 && (
        <div className="hidden">{/*"prev"*/}
          <a href={`/games?category=${selectedCategory}&page=${page - 1}&seo=true`}></a>
        </div>
      )}

      {/* Styling */}
      <style jsx>{`
        .category-btn {
          border-radius: 18px;
          border-width: 1px;
          font-weight: 500;
          transition: all 0.3s ease;
          background-color: transparent; /* Default outline style */
           border-color: #fdba74; /* orange-300 */
           color: #c2410c; /* orange-700 */
        }
         :global(.dark) .category-btn {
           border-color: #9a3412; /* dark:orange-800 */
           color: #fb923c; /* dark:orange-400 */
         }

        .category-btn:hover {
           background-color: #fff7ed; /* orange-50 */
           border-color: #f97316; /* orange-500 */
           color: #ea580c; /* orange-600 */
           transform: translateY(-2px);
           box-shadow: 0 4px 10px rgba(251, 146, 60, 0.2);
        }
         :global(.dark) .category-btn:hover {
           background-color: rgba(154, 52, 18, 0.2); /* dark:orange-900/20 */
           border-color: #fb923c; /* dark:orange-400 */
           color: #fdba74; /* dark:orange-300 */
         }

         .category-btn-active {
           background: linear-gradient(45deg, #f97316, #ea580c); /* orange-500 to orange-600 */
           color: white;
           border-color: transparent;
           box-shadow: 0 5px 15px rgba(234, 88, 12, 0.3);
         }
         :global(.dark) .category-btn-active {
           background: linear-gradient(45deg, #fb923c, #f97316); /* dark:orange-400 to orange-500 */
           color: #5f270e; /* dark:orange-950 approx */
           box-shadow: 0 5px 15px rgba(251, 146, 60, 0.2);
         }
         .category-btn-active:hover { /* Keep active style on hover */
            background: linear-gradient(45deg, #f97316, #ea580c);
            color: white;
            transform: translateY(0);
            box-shadow: 0 5px 15px rgba(234, 88, 12, 0.3);
         }
         :global(.dark) .category-btn-active:hover {
            background: linear-gradient(45deg, #fb923c, #f97316);
            color: #5f270e;
         }

        .game-card {
          position: relative; /* Needed for absolute positioning inside */
          border-radius: 12px; /* Slightly softer corners */
          border: 1px solid #f3f4f6; /* gray-100 */
          background: white;
          transition: all 0.35s cubic-bezier(0.25, 0.8, 0.25, 1);
          /* Subtle inner glow effect */
           box-shadow: inset 0 0 5px rgba(0,0,0,0.02);
        }
         :global(.dark) .game-card {
            border: 1px solid #374151; /* dark:gray-700 */
            background: #1f2937; /* dark:gray-800 */
            box-shadow: inset 0 0 8px rgba(255,255,255,0.03);
         }

        .game-card-link:hover .game-card {
          transform: translateY(-4px) scale(1.02);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08), inset 0 0 5px rgba(0,0,0,0.02);
          border-color: #fdba74; /* orange-300 */
        }
         :global(.dark) .game-card-link:hover .game-card {
           box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2), inset 0 0 8px rgba(255,255,255,0.03);
           border-color: #ea580c; /* dark:orange-600 */
         }

         .game-thumbnail {
           border-radius: 11px 11px 0 0; /* Match card rounding */
           filter: brightness(1);
           transition: filter 0.5s ease-in-out, transform 0.5s ease-in-out;
         }
         .game-card-link:hover .game-thumbnail {
           transform: scale(1.08);
           filter: brightness(1.05);
         }

         .new-badge {
          background: linear-gradient(135deg, #fb923c, #f97316); /* orange-400 to orange-500 */
          color: white;
          font-weight: 600;
          border: none;
          border-radius: 10px 0 10px 0; /* Fancy corner */
          animation: pulse-badge 2s infinite ease-in-out;
         }
         @keyframes pulse-badge {
           0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(249, 115, 22, 0.5); }
           50% { transform: scale(1.05); box-shadow: 0 0 0 5px rgba(249, 115, 22, 0); }
         }

         .game-title {
            letter-spacing: -0.01em;
         }

        .load-more-btn {
           background: linear-gradient(45deg, #ff7b00, #ff9a00);
           color: white;
           border-radius: 20px;
           padding: 0.6rem 1.8rem;
           font-weight: 600;
           transition: all 0.3s ease;
           border: none;
           box-shadow: 0 4px 15px rgba(255, 123, 0, 0.3);
         }
         .load-more-btn:hover:not(:disabled) {
           transform: translateY(-3px);
           box-shadow: 0 6px 20px rgba(255, 123, 0, 0.4);
         }
         .load-more-btn:active:not(:disabled) {
           transform: translateY(-1px);
           box-shadow: 0 3px 10px rgba(255, 123, 0, 0.2);
         }
         .load-more-btn:disabled {
           background: #d1d5db; /* gray-300 */
           box-shadow: none;
           cursor: not-allowed;
           color: #6b7280; /* gray-500 */
         }
          :global(.dark) .load-more-btn:disabled {
             background: #4b5563; /* dark:gray-600 */
             color: #9ca3af; /* dark:gray-400 */
          }

         .spinner {
            display: inline-block;
            width: 1em;
            height: 1em;
            border: 2px solid currentColor;
            border-right-color: transparent;
            border-radius: 50%;
            animation: spinner-spin 0.75s linear infinite;
          }
         @keyframes spinner-spin {
            to { transform: rotate(360deg); }
          }
      `}</style>
    </div>
  );
}
